import { useEffect, useMemo, useRef, useState } from 'react';
import * as d3 from 'd3';
import { darkTheme, lightTheme } from '../theme';
import { useTheme as useSiteTheme } from '../../theme/ThemeContext';
import useResizeObserver from './useResizeObserver';
import { computeLayout } from './layout';
import { buildXScale, buildYScale, buildVolumeYScale } from './scales';
import { drawXAxis, drawYAxis } from './axes';
import { createZoom } from './zoom';
import { computeBarWidth, drawVolumePane } from './volume';
import { createCrosshair, findNearest } from './crosshair';
import { drawCurrentPriceLabel } from './priceLabel';
import OhlcLegendHeader from './OhlcLegendHeader';
import { markRegistry } from '../marks';
import { drawOverlayIndicators } from '../utils/D3OverlayIndicators';
import { drawSubcharts } from '../utils/D3SubCharts';
import { isOverlayIndicator, indicatorTypes } from '../utils/drawUtils/indicatorTypes';

const fmtPrice = d3.format('$,.2f');
const fmtDate = d3.timeFormat('%b %d, %Y');

const normalizeRow = (item) => ({
  ...item,
  date: new Date(item.start_date),
  // Real period_open/high/low/close are always present (confirmed against
  // the backend) -- ?? guards against a legitimate 0, unlike the old `||`
  // fallbacks (which also unnecessarily synthesized fake high/low via
  // *1.02/*0.98 when the real fields were, in practice, never actually
  // missing).
  open: Number(item.period_open ?? item.avg_close ?? 0),
  high: Number(item.period_high ?? item.avg_close ?? 0),
  low: Number(item.period_low ?? item.avg_close ?? 0),
  close: Number(item.avg_close ?? item.period_close ?? 0),
  volume: Number(item.total_volume ?? 0),
});

const toIndicatorKey = (label) => label.toLowerCase().replace(/-/g, '_');

const ChartEngine = ({ markId = 'line', symbol, data, indicatorsData, selectedIndicators = [], onHeightChange, onHover }) => {
  const { theme: siteThemeMode } = useSiteTheme();
  const theme = siteThemeMode === 'light' ? lightTheme : darkTheme;
  const [containerRef, width] = useResizeObserver();
  const svgRef = useRef(null);
  const transformRef = useRef(d3.zoomIdentity);
  const subYScalesRef = useRef({});
  const lastHoveredRef = useRef(null);
  const [hoveredBar, setHoveredBar] = useState(null);

  const mark = markRegistry[markId] || markRegistry.line;

  const normalized = useMemo(() => {
    if (!Array.isArray(data) || data.length === 0) return [];
    const rows = data.map(normalizeRow).sort((a, b) => a.date - b.date);
    return mark.transform ? mark.transform(rows) : rows;
  }, [data, mark]);

  const layout = useMemo(() => computeLayout({ width, selectedIndicators }), [width, selectedIndicators]);

  useEffect(() => {
    onHeightChange?.(layout.totalHeight);
  }, [layout.totalHeight, onHeightChange]);

  // Reset zoom/pan when the symbol or mark type changes -- a fresh series
  // shouldn't inherit a stale zoom window from whatever was viewed before.
  useEffect(() => {
    transformRef.current = d3.zoomIdentity;
  }, [symbol, markId]);

  useEffect(() => {
    if (!svgRef.current || normalized.length === 0 || width === 0) return;

    const svgEl = d3.select(svgRef.current);
    svgEl.selectAll('*').remove();
    svgEl
      .attr('width', width)
      .attr('height', layout.totalHeight)
      .attr('viewBox', `0 0 ${width} ${layout.totalHeight}`)
      .style('display', 'block')
      .style('background', theme.background.chart);

    const defs = svgEl.append('defs');
    defs.append('clipPath').attr('id', 'price-clip').append('rect').attr('width', layout.availableWidth).attr('height', layout.price.height);
    defs.append('clipPath').attr('id', 'volume-clip').append('rect').attr('width', layout.availableWidth).attr('height', layout.volume.height);

    const root = svgEl.append('g').attr('transform', `translate(${layout.margin.left},${layout.margin.top})`);

    const baseXScale = buildXScale(normalized, layout.availableWidth);
    const priceYScale = buildYScale(mark.getYExtent(normalized), layout.price.height);
    const volumeYScale = buildVolumeYScale(normalized, layout.volume.height);

    const pricePane = root.append('g').attr('transform', `translate(0,${layout.price.top})`);
    const priceContent = pricePane.append('g').attr('clip-path', 'url(#price-clip)');
    const overlayContent = pricePane.append('g').attr('clip-path', 'url(#price-clip)');
    const priceLabelGroup = pricePane.append('g');
    const priceYAxisGroup = pricePane.append('g');

    const volumePane = root.append('g').attr('transform', `translate(0,${layout.volume.top})`);
    const volumeContent = volumePane.append('g').attr('clip-path', 'url(#volume-clip)');

    root
      .append('line')
      .attr('x1', 0)
      .attr('x2', layout.availableWidth)
      .attr('y1', layout.volume.top - 4)
      .attr('y2', layout.volume.top - 4)
      .attr('stroke', theme.grid.color)
      .attr('opacity', 0.5);

    const subWrap = root.append('g').attr('class', 'subpanes');

    const totalContentHeight = layout.subpanes.length > 0
      ? layout.subpanes[layout.subpanes.length - 1].top + layout.subpanes[layout.subpanes.length - 1].height
      : layout.volume.top + layout.volume.height;

    // Bottom x-axis: attached directly under volume only when there are no
    // subpanes (D3SubCharts.js draws its own x-axis on the last subpane
    // when they exist, avoiding a duplicate).
    const bottomAxisGroup = layout.subpanes.length === 0
      ? root.append('g').attr('transform', `translate(0,${totalContentHeight})`)
      : null;

    function render(xScale) {
      // Count only the bars actually inside the current (post-zoom/pan) x
      // domain -- computeBarWidth's own doc comment already called for
      // this, but every caller passed normalized.length (the whole
      // dataset) instead, which is why zooming in never made candles
      // wider. d3.bisector keeps this a binary search rather than a full
      // scan on every zoom tick.
      const [domainStart, domainEnd] = xScale.domain();
      const bisectDate = d3.bisector((d) => d.date).left;
      const visibleStart = bisectDate(normalized, domainStart);
      const visibleEnd = bisectDate(normalized, domainEnd);
      const visibleCount = Math.max(1, visibleEnd - visibleStart);
      const barWidth = computeBarWidth({ innerWidth: layout.availableWidth, visibleCount });

      drawYAxis({ group: priceYAxisGroup, yScale: priceYScale, availableWidth: layout.availableWidth, theme });
      if (bottomAxisGroup) {
        drawXAxis({ group: bottomAxisGroup, xScale, height: totalContentHeight, availableWidth: layout.availableWidth, theme });
      }

      mark.draw({ group: priceContent, data: normalized, xScale, yScale: priceYScale, theme, barWidth, defs });
      drawVolumePane({ group: volumeContent, data: normalized, xScale, yScale: volumeYScale, theme, barWidth });

      overlayContent.selectAll('*').remove();
      const overlayIndicators = selectedIndicators
        .filter(isOverlayIndicator)
        .map((label) => ({ name: label, type: indicatorTypes[label], values: indicatorsData?.[toIndicatorKey(label)] }));
      if (overlayIndicators.length > 0) {
        drawOverlayIndicators(overlayContent, normalized, xScale, priceYScale, overlayIndicators);
      }

      drawCurrentPriceLabel({
        group: priceLabelGroup,
        yScale: priceYScale,
        availableWidth: layout.availableWidth,
        latest: normalized[normalized.length - 1],
        prev: normalized[normalized.length - 2],
        theme,
      });

      subWrap.selectAll('*').remove();
      if (layout.subpanes.length > 0) {
        const { yScales } = drawSubcharts({
          root: subWrap,
          panes: layout.subpanes,
          data: normalized,
          xScale,
          availableWidth: layout.availableWidth,
          findicatorsData: indicatorsData,
          theme,
        });
        subYScalesRef.current = yScales;
      } else {
        subYScalesRef.current = {};
      }
    }

    const crosshair = createCrosshair(root, { totalHeight: totalContentHeight, theme });

    function resolveActivePane(mouseY) {
      if (mouseY <= layout.volume.top + layout.volume.height) {
        return { top: layout.price.top, height: layout.price.height, yScale: priceYScale, format: fmtPrice };
      }
      for (const sp of layout.subpanes) {
        if (mouseY >= sp.top && mouseY <= sp.top + sp.height) {
          return { top: sp.top, height: sp.height, yScale: subYScalesRef.current[sp.id] || priceYScale, format: d3.format('.2f') };
        }
      }
      return { top: layout.price.top, height: layout.price.height, yScale: priceYScale, format: fmtPrice };
    }

    const overlay = root
      .append('rect')
      .attr('width', layout.availableWidth)
      .attr('height', totalContentHeight)
      .attr('fill', 'none')
      .attr('pointer-events', 'all')
      // Without this, a touch-drag on the chart is ambiguous between "the
      // page's native scroll" and "d3-zoom's own pan gesture" -- browsers
      // resolve that race inconsistently. touch-action: none hands the
      // whole gesture to d3-zoom explicitly, matching how every real
      // trading chart's canvas behaves (drag pans the chart, never the
      // page) instead of fighting page scroll on a phone.
      .style('touch-action', 'none');

    overlay
      .on('mousemove', (event) => {
        const [mouseX, mouseY] = d3.pointer(event);
        const currentX = transformRef.current.rescaleX(baseXScale);
        const nearest = findNearest(normalized, currentX.invert(mouseX));
        if (!nearest) return;

        const pane = resolveActivePane(mouseY);
        const clampedY = Math.max(pane.top, Math.min(pane.top + pane.height, mouseY));
        const localY = clampedY - pane.top;
        const value = pane.yScale.invert(localY);

        crosshair.moveTo({
          mouseX,
          availableWidth: layout.availableWidth,
          pane,
          valueY: localY,
          priceLabel: pane.format(value),
          dateLabel: fmtDate(nearest.date),
        });

        if (lastHoveredRef.current !== nearest) {
          lastHoveredRef.current = nearest;
          setHoveredBar(nearest);
          onHover?.(nearest);
        }
      })
      .on('mouseleave', () => {
        crosshair.hide();
        lastHoveredRef.current = null;
        setHoveredBar(null);
        onHover?.(null);
      });

    const zoomBehavior = createZoom({
      availableWidth: layout.availableWidth,
      height: totalContentHeight,
      onZoom: (transform) => {
        transformRef.current = transform;
        render(transform.rescaleX(baseXScale));
      },
    });

    overlay.call(zoomBehavior);
    zoomBehavior.transform(overlay, transformRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalized, layout, mark, selectedIndicators, indicatorsData, theme]);

  const activeBar = hoveredBar || normalized[normalized.length - 1];
  const activeIndex = activeBar ? normalized.indexOf(activeBar) : -1;
  const prevBar = activeIndex > 0 ? normalized[activeIndex - 1] : undefined;

  return (
    <div ref={containerRef} className="relative w-full" style={{ height: layout.totalHeight, background: theme.background.chart }}>
      <svg ref={svgRef} className="w-full h-full chart-engine-svg" />
      <OhlcLegendHeader symbol={symbol} bar={activeBar} prevBar={prevBar} markLabel={mark.label} />
    </div>
  );
};

export default ChartEngine;
